/*
 * AKAZIE-Anpassung (Claude 29.09.2026, analog RobinienWelt DE 25.09.2026):
 * Variantenprodukte ohne alle Optionskombinationen (z. B. Höhe + Ausführung + Verpackungseinheit).
 * - Wählt der Kunde einen Wert, zu dem es mit den übrigen Optionen keine Variante gibt,
 *   werden die übrigen Optionen automatisch auf die passende vorhandene Variante gesetzt
 *   (kaufbar bevorzugt, dann möglichst viele gleiche Optionen).
 * - Ein Optionswert wird nur dann als "nicht verfügbar" markiert, wenn es mit ihm
 *   überhaupt keine kaufbare Variante gibt.
 * Original: assets/variant-selects.js des Themes Hyper (MD5 dc80c3ed786102345a10765d484966ad).
 */
window.AkzVariantData = window.AkzVariantData || {};

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.akzLoadVariants().then(() => this.akzMarkAvailability());
        if (!this.akzObserver) {
          let t = null;
          this.akzObserver = new MutationObserver(() => {
            clearTimeout(t);
            t = setTimeout(() => this.akzMarkAvailability(), 50);
          });
          this.akzObserver.observe(this, { childList: true, subtree: true });
        }

        this.addEventListener('change', (event) => {
          this.updateSelectedSwatchValue(event);
          this.akzSelectClosestVariant(event.target);
          const target = this.getInputForEventTarget(event.target);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      /* ---------- AKAZIE: Variantendaten ---------- */

      akzProductUrl() {
        return (this.dataset.url || '').split('?')[0];
      }

      akzLoadVariants() {
        const url = this.akzProductUrl();
        if (!url) return Promise.resolve(null);
        if (!window.AkzVariantData[url]) {
          window.AkzVariantData[url] = fetch(`${url}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => {
              const v = p && Array.isArray(p.variants) ? p.variants : null;
              window.AkzVariantData[url + '#sync'] = v;
              return v;
            })
            .catch(() => null);
        }
        return window.AkzVariantData[url];
      }

      akzGroups() {
        return Array.from(this.querySelectorAll('.product-form__input')).filter((g) =>
          g.querySelector('input[type="radio"], select')
        );
      }

      akzGroupValue(group) {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }

      akzSetGroupValue(group, value) {
        const select = group.querySelector('select');
        if (select) {
          const opt = Array.from(select.options).find((o) => o.value === value);
          if (!opt) return;
          Array.from(select.options).forEach((o) => o.removeAttribute('selected'));
          opt.setAttribute('selected', 'selected');
          select.value = value;
          this.updateSelectedSwatchValue({ target: select });
          return;
        }
        const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((i) => i.value === value);
        if (!input) return;
        input.checked = true;
        this.updateSelectedSwatchValue({ target: input });
        const label = group.querySelector('[data-selected-swatch-value]');
        if (label) label.textContent = value;
      }

      akzSelectClosestVariant(changedEl) {
        // synchron: die mitlaufende Kaufleiste gleicht die Auswahl sofort nach dem Event ab
        const variants = window.AkzVariantData[this.akzProductUrl() + '#sync'];
        if (!variants || !variants.length) return;
        const groups = this.akzGroups();
        if (groups.length < 2) return;

        const current = groups.map((g) => this.akzGroupValue(g));
        const exists = variants.some((v) => groups.every((_, i) => v.options[i] === current[i]));
        if (exists) return;

        const changedGroup = changedEl ? changedEl.closest('.product-form__input') : null;
        const idx = groups.indexOf(changedGroup);
        let candidates = idx >= 0 ? variants.filter((v) => v.options[idx] === current[idx]) : variants;
        if (!candidates.length) return;

        let best = null;
        let bestScore = -1;
        candidates.forEach((v) => {
          let score = v.available ? 1000 : 0;
          groups.forEach((_, i) => {
            if (v.options[i] === current[i]) score += 10 * (groups.length - i);
          });
          if (score > bestScore) {
            bestScore = score;
            best = v;
          }
        });
        if (!best) return;
        groups.forEach((g, i) => {
          if (best.options[i] !== current[i]) this.akzSetGroupValue(g, best.options[i]);
        });
      }

      async akzMarkAvailability() {
        const variants = await this.akzLoadVariants();
        if (!variants || !variants.length) return;
        this.akzGroups().forEach((group, i) => {
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const buyable = variants.some((v) => v.available && v.options[i] === input.value);
            input.classList.toggle('disabled', !buyable);
          });
        });
      }

      /* ---------- Original Hyper ---------- */

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          const prev = Array.from(target.options).find((option) => option.getAttribute('selected'));
          if (prev) prev.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
